import { useUserActions } from '../../auth/hooks/useUserActions';

/**
 * Master list of feature flag names. Add a flag here to make it usable
 * in code via `useFeatureFlags('flagName')` with full type-safety.
 *
 * Keep names in sync with the `name` column managed in the
 * super-admin "Feature Flags" page.
 */
const FEATURE_FLAGS = [
  'reportVisibility',
  'levelOneFeedback',
  'programProfiling',
  'programsAndReports',
  'claimAnInvestment',
  'needToRestartImpactModel',
  'cnReportFeedback',
  'snapshot',
  'cnTaskUpdates',
] as const;

type FeatureFlagName = typeof FEATURE_FLAGS[number];

export type FeatureFlags = Partial<Record<FeatureFlagName, boolean>> &
  Record<string, boolean>;

/**
 * Server returns features as [{ name, value }, ...]; flatten to a map
 * so it can be read as user.attributes.features[name] in the app.
 */
export const normalizeFeatures = (raw: unknown): FeatureFlags =>
  Array.isArray(raw)
    ? raw.reduce<FeatureFlags>((acc, f) => {
        if (f && typeof f.name === 'string') acc[f.name] = Boolean(f.value);
        return acc;
      }, {})
    : ((raw as FeatureFlags) || {});

export function useFeatureFlags(name: FeatureFlagName): boolean;
export function useFeatureFlags(...names: FeatureFlagName[]): boolean[];
export function useFeatureFlags(
  ...names: FeatureFlagName[]
): boolean | boolean[] {
  const { user } = useUserActions();
  const features = user?.attributes?.features || {};
  const flags = names.map(name => Boolean(features[name]));
  return flags.length === 1 ? flags[0] : flags;
}
